import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomInput from "../../../../../../components/common/Input/input";
import SettingsBreadcrumb from "../../../../../../components/SettingsBreadcrumb";
import BibliotecaModal from "../../BibliotecaModal/BibliotecaModal";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Flex,
  Heading,
  Box,
  Button,
  FormLabel,
  FormControl,
  Image,
  IconButton,
  Spinner,
  Select,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import * as api from "../../../../../../api/outrasPaginas";
import Swal from "sweetalert2";
import { FaImage, FaTimes, FaEye } from "react-icons/fa";
import "./editar-modelo-4.scss";

interface Imagem {
  id: number;
  imagem: string;
  imagemPreview: string;
  posicaoImagem: string;
  tipo?: 'image' | 'video';
}

interface Secao {
  id: number;
  titulo: string;
  descricao: string;
  aberto: boolean;
  imagem: Imagem[];
}

const EditarModelo4: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<{
    titulo: { value: string; error: boolean };
    dscUrl: { value: string; error: boolean };
  }>({
    titulo: { value: "", error: false },
    dscUrl: { value: "", error: false }
  });

  const [isLoadingButton, setLoadingButton] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [bibliotecaOpen, setBibliotecaOpen] = useState<boolean>(false);
  const [imagens, setImagens] = useState<Imagem[]>([]);
  const [listaPosicaoImagem, setListaPosicaoImagem] = useState<
    { codPropriedade: number; dscPropriedade: string }[]
  >([]);
  const videoExts = ['.mp4', '.webm', '.ogg'];

  const {
    isOpen: isPreviewAberto,
    onOpen: onPreviewAberto,
    onClose: onPreviewFechado
  } = useDisclosure()

  useEffect(() => {
    api
      .listarPosicaoImagem()
      .then((data) => setListaPosicaoImagem(data))
      .catch((err) =>
        console.error("Erro ao listar posições de imagem", err)
      );
  }, []);

  useEffect(() => {
    fetchObterPagina();
  }, []);

  const fetchObterPagina = async () => {
    setIsLoading(true);
    try {
      const paginasData = await api.obterPagina(Number(id));
      setFormValues({
        titulo: { value: paginasData.nomPagina, error: false },
        dscUrl: { value: paginasData.dscUrl, error: false }
      });

      if (paginasData.jsonModelo) {
        const parsed = JSON.parse(paginasData.jsonModelo);

        const imagensFlat: Imagem[] = [];

        if (Array.isArray(parsed.imagens) && parsed.imagens.length) {
          if (parsed.imagens[0].imagem) {
            parsed.imagens.forEach((secao: any) => {
              if (Array.isArray(secao.imagem)) {
                secao.imagem.forEach((img: any) => {
                  const url = img.url as string;
                  const limpar = url.split('?')[0].toLowerCase();
                  const isVideo = videoExts.some(ext => limpar.endsWith(ext));

                  imagensFlat.push({
                    id: img.id,
                    imagem: url,
                    imagemPreview: url,
                    posicaoImagem: (
                      img.codPropriedade ?? img.posicaoImagem
                    )?.toString() || '',
                    tipo: isVideo ? 'video' : 'image'
                  });
                });
              }
            });
          }
          else {
            parsed.imagens.forEach((img: any) => {
              const url = img.url as string;
              const limpar = url.split('?')[0].toLowerCase();
              const isVideo = videoExts.some(ext => limpar.endsWith(ext));

              imagensFlat.push({
                id: img.id,
                imagem: url,
                imagemPreview: url,
                posicaoImagem: (
                  img.codPropriedade ?? ''
                ).toString(),
                tipo: isVideo ? 'video' : 'image'
              });
            });
          }
        }

        setImagens(imagensFlat);
      }
    } catch (error) {
      console.error("Erro ao buscar dados da página:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const abrirBlibliotecaDeFotos = () => {
    setBibliotecaOpen(true);
  };

  const handleSelectImageFromBiblioteca = (media: any) => {
    const url = decodeURIComponent(media.dscUriCdn || media.dscMidia);
    const limpar = url.split('?')[0].toLowerCase();

    const isVideo = videoExts.some(ext => limpar.endsWith(ext));

    const novaImagem: Imagem = {
      id: Date.now(),
      imagem: url,
      imagemPreview: url,
      posicaoImagem: '',
      tipo: isVideo ? 'video' : 'image'
    };

    setImagens(prev => [...prev, novaImagem]);
    setBibliotecaOpen(false);
  };

  const handleFieldChange = (field: keyof typeof formValues, value: any) => {
    setFormValues({ ...formValues, [field]: { value, error: false } });
  };

  const isFormValid = () => {
    const tituloValue = formValues.titulo.value.trim();
    const dscUrlValue = formValues.dscUrl.value.trim();
    if (!tituloValue || !dscUrlValue) return false;
    return imagens.length > 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    for (const img of imagens) {
      if (img.imagem && !img.posicaoImagem) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Selecione uma opção válida para a posição de todas as imagens.",
          confirmButtonText: "OK"
        });
        return;
      }
    }

    setLoadingButton(true);
    try {
      const updatedImagens = imagens.map((img) => {
        const posicao = listaPosicaoImagem.find(
          (option) => option.codPropriedade.toString() === img.posicaoImagem
        );
        return {
          id: img.id,
          url: img.imagem,
          codPropriedade: posicao ? posicao.codPropriedade : null,
          dscPropriedade: posicao ? posicao.dscPropriedade : null
        };
      });

      const jsonModeloData = {
        codModeloPagina: 4,
        tituloGeral: formValues.titulo.value,
        imagens: updatedImagens
      };

      const params = {
        seqPagina: Number(id),
        nomPagina: formValues.titulo.value || null,
        dscUrl: formValues.dscUrl.value,
        txtHtml: null,
        numHtmlProprio: 0,
        numBloqueada: 0,
        sitRegistro: 1,
        datInativo: null,
        codModeloPagina: 4,
        jsonModelo: JSON.stringify(jsonModeloData)
      };

      await api.editarPagina(params);
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Página editada com sucesso.",
        confirmButtonColor: "#1789FC",
        confirmButtonText: "OK"
      }).then(() => {
        navigate("/cms/edicao-cidade-inteligente/outras-paginas");
      });
    } catch (error) {
      console.error("Erro ao editar página:", error);
      Swal.fire({
        icon: "error",
        title: "Ocorreu um erro!",
        text: "Erro ao editar página. Tente novamente mais tarde.",
        confirmButtonColor: "#1789FC",
        confirmButtonText: "OK"
      });
    } finally {
      setLoadingButton(false);
    }
  };

  const handleRemoveImagem = (imgId: number) => {
    setImagens((prev) => prev.filter((img) => img.id !== imgId));
  };

  const handleAtualizarPosicaoImagem = (imgId: number, posicao: string) => {
    setImagens((prev) =>
      prev.map((img) =>
        img.id === imgId ? { ...img, posicaoImagem: posicao } : img
      )
    );
  };

  const carregarPreview = () => {
    const todasImagens = imagens.map(img => ({
      ...img,
      ordem:
        listaPosicaoImagem.find(p => p.codPropriedade.toString() === img.posicaoImagem)?.dscPropriedade?.toLowerCase() || 'centro'
    }));

    const esquerda = todasImagens.filter(i => i.ordem.includes('esquerda'));
    const centro = todasImagens.filter(i => i.ordem.includes('centro') || !i.ordem);
    const direita = todasImagens.filter(i => i.ordem.includes('direita'));

    const ordenadas = [...esquerda, ...centro, ...direita];

    return (
      <Box px={[4, 10]} py={8} maxW="1200px" mx="auto">
        <Heading as="h2" fontSize="2xl" color="#004a7f" mb={8} textAlign="center">
          {formValues.titulo.value}
        </Heading>

        <Flex justify="center" wrap="wrap" gap={6}>
          {ordenadas.map((img) => (
            <Box
              key={img.id}
              maxW="300px"
              w="100%"
              borderRadius="lg"
              boxShadow="md"
              overflow="hidden"
              bg="#f4f4f4"
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              p="4"
            >
              {img.tipo === 'video' ? (
                <video
                  src={img.imagemPreview}
                  controls
                  muted
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    border: '2px solid #ccc',
                    borderRadius: '8px'
                  }}
                />
              ) : (
                <Image
                  src={img.imagemPreview}
                  alt="Mídia"
                  maxW="100%"
                  maxH="300px"
                  objectFit="contain"
                  border="1px solid #ccc"
                  borderRadius="8px"
                />
              )}
            </Box>
          ))}
        </Flex>
      </Box>
    );
  };

  return (
    <Flex
      direction="column"
      padding="40px"
      backgroundColor="#fafafa"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      <SettingsBreadcrumb />
      <Heading
        mt="30px"
        fontWeight="600"
        fontSize="21px"
        color="#6F6F6F"
        w="100%"
      >
        Editar Modelo 4 - Mídia Livre
      </Heading>
      {isLoading ? (
        <Flex
          justify="center"
          align="center"
          direction="column"
          w="70%"
          h="90%"
          position='fixed'
        >
          <Spinner size="xl" color="blue.500" />
        </Flex>
      ) : (
        <Box m="40px 0" w="100%">
          <form onSubmit={handleFormSubmit}>
            <div className="row mb-3">
              <div className="col-md-6 mb-4">
                <FormControl isRequired isInvalid={formValues.titulo.error}>
                  <FormLabel mb="8px">Título Geral</FormLabel>
                  <CustomInput
                    placeholder="Digite um título"
                    value={formValues.titulo.value}
                    maxLength={100}
                    onChange={(e) => handleFieldChange("titulo", e.target.value)}
                  />
                </FormControl>
              </div>
              <div className="col-md-6 mb-4">
                <FormControl isRequired isInvalid={formValues.dscUrl.error}>
                  <FormLabel mb="8px">URL da Página</FormLabel>
                  <CustomInput
                    placeholder="Digite uma URL"
                    value={formValues.dscUrl.value}
                    onChange={(e) => handleFieldChange("dscUrl", e.target.value)}
                    maxLength={512}
                  />
                </FormControl>
              </div>
            </div>

            <FormControl mb="20px">
              <FormLabel>Mídia</FormLabel>
              <Flex wrap="wrap" gap="20px">
                {imagens.map((img) => (
                  <Box
                    key={img.id}
                    borderWidth="1px"
                    borderRadius="md"
                    p="10px"
                    bg="#FFF"
                    boxShadow="md"
                    position="relative"
                  >
                    <Box
                      w="150px"
                      h="100px"
                      bg="#f0f0f0"
                      borderRadius="4px"
                      overflow="hidden"
                      mx="auto"
                    >
                      {img.tipo === 'video' ? (
                        <video
                          src={img.imagemPreview}
                          controls
                          muted
                          playsInline
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <Image
                          src={img.imagemPreview}
                          alt="Preview da mídia"
                          w="100%"
                          h="100%"
                          objectFit="contain"
                          display="block"
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                    </Box>
                    <IconButton
                      icon={<FaTimes />}
                      size="xs"
                      colorScheme="red"
                      position="absolute"
                      top="2"
                      right="2"
                      borderRadius="full"
                      aria-label="Remover imagem"
                      onClick={() => handleRemoveImagem(img.id)}
                    />
                    <Select
                      mt="10px"
                      value={img.posicaoImagem}
                      onChange={(e) =>
                        handleAtualizarPosicaoImagem(img.id, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Posição
                      </option>
                      {listaPosicaoImagem.map((option) => (
                        <option
                          key={option.codPropriedade}
                          value={option.codPropriedade}
                        >
                          {option.dscPropriedade}
                        </option>
                      ))}
                    </Select>
                  </Box>
                ))}
                <Button
                  variant="outline"
                  onClick={abrirBlibliotecaDeFotos}
                >
                  <FaImage color="gray" size="15px" />
                  <Text pl="6px" mt="4px" fontSize="sm">
                    Adicionar Mídia
                  </Text>
                </Button>
              </Flex>
            </FormControl>

            <Flex mt="35px" justifyContent="end">
              <Button
                m="6px"
                onClick={() =>
                  navigate("/cms/edicao-cidade-inteligente/outras-paginas")
                }
              >
                Desistir
              </Button>
              <Button
                mt="6px"
                leftIcon={<FaEye />}
                variant="outline"
                onClick={onPreviewAberto}
              >
                Ver Preview
              </Button>
              <Button
                type="submit"
                m="6px"
                color="#FFF"
                bgColor="#1665D8"
                _hover={{ opacity: 0.8 }}
                isDisabled={!isFormValid()}
                isLoading={isLoadingButton}
                loadingText="Editando..."
              >
                Editar Página Modelo
              </Button>
            </Flex>
          </form>
        </Box>
      )}
      <BibliotecaModal
        isOpen={bibliotecaOpen}
        onClose={() => setBibliotecaOpen(false)}
        onSelectImage={handleSelectImageFromBiblioteca}
      />

      <Modal isOpen={isPreviewAberto} onClose={onPreviewFechado} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={'#004a7f'} fontWeight={'bold'}></ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {carregarPreview()}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPreviewFechado}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default EditarModelo4;
